import React from 'react';
import { useLocalMediaContext } from '../../contexts/local-media-context';
import { Button } from '../ui/button';
import { RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * MediaSyncStatus Component
 *
 * Displays the sync status of user media files from Convex to IndexedDB.
 * Shows progress, errors, and provides a manual refresh button.
 */
export function MediaSyncStatus() {
  const { syncStatus, syncProgress, syncError, lastSyncTime, syncFromConvex } =
    useLocalMediaContext();

  const handleManualSync = () => {
    syncFromConvex();
  };

  // Syncing state - show progress indicator
  if (syncStatus === 'syncing') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" />
        <span>
          Syncing files... {syncProgress.current}/{syncProgress.total}
        </span>
      </div>
    );
  }

  // Error state - show error message with retry button
  if (syncStatus === 'error') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-destructive">
        <AlertCircle className="h-4 w-4" />
        <span>Sync failed: {syncError}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleManualSync}
          className="ml-auto"
        >
          Retry
        </Button>
      </div>
    );
  }

  // Completed state - show last sync time with refresh button
  if (syncStatus === 'completed' && lastSyncTime) {
    const minutesAgo = Math.floor((Date.now() - lastSyncTime) / 60000);
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <span>
          Synced {minutesAgo === 0 ? 'just now' : `${minutesAgo}m ago`}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleManualSync}
          className="ml-auto"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>
    );
  }

  // Idle state - show manual refresh button
  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleManualSync}
        className="ml-auto"
      >
        <RefreshCw className="h-4 w-4" />
        Sync Files
      </Button>
    </div>
  );
}
